/**
 * @file SuspendedAgencyScreen.jsx
 * @description Full-screen gate shown when an agency or therapist account has been
 *   suspended by the platform. Provides context-appropriate messaging and a support
 *   contact modal. Mirrors the pattern used by AccountDeletionPending.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module components/SuspendedAgencyScreen
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import { getCachedConfig } from "../lib/configService";
import ContactSupportModal from "./ContactSupportModal";
import { LockIcon } from "./Icons";


export default function SuspendedAgencyScreen({ profile, onSignOut }) {
  const { t } = useTranslation();
  const [showSupport, setShowSupport] = useState(false);
  const name    = profile?.full_name || "your account";
  const role    = profile?.role;
  const isWorker = role === "rbt" || role === "bcba" || role === "bcaba";
  const configMsg = getCachedConfig(isWorker ? "suspension_worker_portal_message" : "suspension_portal_message");

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #1E1B4B 0%, #312E81 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24,
      fontFamily: "'Inter','DM Sans',sans-serif",
    }}>
      <div style={{
        background: T.surface,
        borderRadius: 20,
        padding: "48px 40px 40px",
        maxWidth: 480, width: "100%",
        boxShadow: "0 32px 80px rgba(0,0,0,0.4)",
        textAlign: "center",
      }}>
        {/* Icon */}
        <div style={{
          width: 72, height: 72, borderRadius: "50%",
          background: "#fef2f2", border: "2px solid #fca5a5",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 24px",
        }}>
          <LockIcon size={32} color="#ef4444" />
        </div>

        <div style={{ fontSize: 22, fontWeight: 800, color: T.text, marginBottom: 8 }}>
          {t('suspended.title')}
        </div>
        <div style={{ fontSize: 13, color: T.textMid, marginBottom: 24, lineHeight: 1.7 }}>
          {configMsg || t('suspended.subtitle')}
        </div>

        {/* Support CTA */}
        <button
          type="button"
          onClick={() => setShowSupport(true)}
          style={{
            display: "block", width: "100%",
            padding: "12px 20px",
            borderRadius: 10,
            background: T.blue,
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            border: "none",
            cursor: "pointer",
            marginBottom: 14,
            fontFamily: "'Inter','DM Sans',sans-serif",
          }}
        >
          {t('suspended.contactButton')}
        </button>
        {showSupport && (
          <ContactSupportModal
            profile={profile}
            defaultTopic="account_suspended"
            onClose={() => setShowSupport(false)}
          />
        )}

        <button
          type="button"
          onClick={onSignOut}
          style={{
            display: "block", width: "100%",
            padding: "10px 20px", borderRadius: 10,
            border: `1px solid ${T.border}`,
            background: T.surface, color: T.textMid,
            fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}
        >
          {t('suspended.signOut')}
        </button>

        <div style={{ fontSize: 11, color: T.textLight, marginTop: 20, lineHeight: 1.5 }}>
          Signed in as <strong>{name}</strong>
        </div>
      </div>
    </div>
  );
}
